import { useMutation, useQuery } from "@tanstack/react-query";

import { QUERY_KEYS } from "@/api/constants";
import { queryClient } from "@/api/queryClient";
import { RESOURCE_QUERY_META } from "@/api/resourceQuery";
import { clearWizardDraft } from "@/modules/wizardDraft";
import type { PaginationParams } from "@/types/api";

import type { GroupId } from "../groups";
import { brandsApi } from "./brandApi";
import {
  ensureMenuExists,
  menuQueryKey,
  setMenuQueryData,
} from "./menuQueryUtils";
import type {
  BrandId,
  BrandMenuPatch,
  IBrandGetById,
  IBrandPatchPayload,
} from "./types";

export const useDeleteBrandMutation = () => {
  return useMutation({
    mutationFn: (id: BrandId) => {
      return brandsApi.deleteBrand(id);
    },
    onSuccess: (_data, id) => {
      clearWizardDraft("brand", id);
    },
  });
};

export const useUpdateBrandMutation = () => {
  return useMutation({
    mutationFn: ({ id, data }: { id: BrandId; data: IBrandPatchPayload }) => {
      return brandsApi.patchBrand(id, data);
    },
  });
};

export const useGetAllBrandsQuery = (
  params?: PaginationParams,
  options = {}
) => {
  return useQuery({
    queryKey: [QUERY_KEYS.brands, params],
    queryFn: () => brandsApi.getAllBrands(params),
    ...options,
  });
};

export const useGetBrandByIdQuery = (id: BrandId) => {
  return useQuery<IBrandGetById>({
    queryKey: [QUERY_KEYS.brands, id],
    queryFn: () => brandsApi.getBrandById(id),
    enabled: !!id,
    meta: RESOURCE_QUERY_META,
  });
};

export const useGetBrandAvailableLegalEntityByGroupId = (id: GroupId) => {
  return useQuery({
    queryKey: [QUERY_KEYS.brandAvailableLegalEntity, id],
    queryFn: () => brandsApi.getBrandAvailableLegalEntityByGroupId(id),
    enabled: !!id,
  });
};

export const useGetMenuQuery = (brandId: BrandId) => {
  return useQuery({
    queryKey: menuQueryKey(brandId),
    queryFn: () => brandsApi.getMenu(brandId),
    enabled: !!brandId,
  });
};

export const usePatchMenuMutation = () => {
  return useMutation({
    mutationFn: async ({
      brandId,
      menu,
    }: {
      brandId: BrandId;
      menu: BrandMenuPatch;
    }) => {
      await ensureMenuExists(brandId);
      return brandsApi.patchMenu(brandId, menu);
    },
    onSuccess: (menu, { brandId }) => {
      setMenuQueryData(brandId, menu);
      queryClient.invalidateQueries({
        queryKey: menuQueryKey(brandId),
      });
    },
  });
};

export const useDeleteMenuMutation = () => {
  return useMutation({
    mutationFn: (brandId: BrandId) => brandsApi.resetMenu(brandId),
    onSuccess: (menu, brandId) => {
      setMenuQueryData(brandId, menu);
    },
  });
};

export const useParseMenuFromUrlMutation = () => {
  return useMutation({
    mutationFn: ({ url }: { url: string }) => brandsApi.parseMenuFromUrl(url),
  });
};

export const useParseMenuFromFileMutation = () => {
  return useMutation({
    mutationFn: ({ file }: { file: File }) => brandsApi.parseMenuFromFile(file),
  });
};
